import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { InternshipResult } from '../../../../types/internship';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';

interface WithdrewFormProps {
  data: Partial<InternshipResult>;
  onUpdate: (updates: Partial<InternshipResult>) => void;
  focus: CareerFocusWithMetrics;
}

const WithdrewForm: React.FC<WithdrewFormProps> = ({
  data,
  onUpdate
}) => {
  const { darkMode } = useDarkMode();
  const [withdrawReason, setWithdrawReason] = useState(data.rejectionReason || '');
  const [alternativePath, setAlternativePath] = useState(
    data.lessonsLearned?.startsWith('Alternative path: ') 
      ? data.lessonsLearned.replace('Alternative path: ', '') 
      : ''
  );
  const [additionalNotes, setAdditionalNotes] = useState(data.feedbackReceived || '');
  
  const bgColor = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const inputBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-300';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  
  // Update parent component whenever state changes
  useEffect(() => {
    // Only update if values have actually changed from what was passed in
    const currentLessonsLearned = alternativePath ? `Alternative path: ${alternativePath}` : undefined;
    const hasChanges = 
      withdrawReason !== (data.rejectionReason || '') ||
      additionalNotes !== (data.feedbackReceived || '') ||
      currentLessonsLearned !== data.lessonsLearned;
    
    if (hasChanges) {
      onUpdate({
        rejectionReason: withdrawReason,
        feedbackReceived: additionalNotes,
        // Store alternative path in lessons learned for now
        lessonsLearned: currentLessonsLearned
      });
    }
  }, [withdrawReason, alternativePath, additionalNotes]);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-xl font-semibold mb-2 ${textColor}`}>
          Your Decision to Withdraw
        </h2>
        <p className={mutedTextColor}>
          Sometimes the best decision is to pursue a different path. Let's capture your reasoning.
        </p>
      </div>
      
      {/* Withdraw Reason */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${textColor}`}>
          Primary reason for withdrawing
        </label>
        <div className="space-y-2">
          {[
            { value: 'better_opportunity', label: 'Accepted a better opportunity' },
            { value: 'personal_reasons', label: 'Personal reasons' },
            { value: 'culture_fit', label: 'Not the right culture fit' },
            { value: 'role_mismatch', label: 'Role was different than expected' },
            { value: 'location', label: 'Location/commute issues' },
            { value: 'compensation', label: 'Compensation concerns' },
            { value: 'other', label: 'Other reason' }
          ].map(option => (
            <label key={option.value} className="flex items-center">
              <input
                type="radio"
                value={option.value}
                checked={withdrawReason === option.value}
                onChange={(e) => setWithdrawReason(e.target.value)}
                className="mr-3"
              />
              <span className={textColor}>{option.label}</span>
            </label>
          ))}
        </div>
      </div>
      
      {/* Alternative Path */}
      {withdrawReason === 'better_opportunity' && (
        <div>
          <label className={`block text-sm font-medium mb-2 ${textColor}`}>
            What opportunity did you pursue instead? (Optional)
          </label>
          <input
            type="text"
            value={alternativePath}
            onChange={(e) => setAlternativePath(e.target.value)}
            placeholder="e.g., Different company, graduate school, startup"
            className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor}`}
          />
        </div>
      )}
      
      {/* Additional Notes */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${textColor}`}>
          Additional context or reflections (Optional)
        </label>
        <textarea
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          rows={4}
          placeholder="Share any additional thoughts about your decision..."
          className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500`}
        />
      </div>
      
      {/* Positive Note */}
      <div className={`p-4 rounded-lg ${bgColor}`}>
        <p className={`text-sm ${textColor}`}>
          🌟 Making the right decision for your career takes courage. This experience still provided valuable insights and growth.
        </p>
      </div>
    </div>
  );
};

export default WithdrewForm;